import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type AgentModeView, toolChoices } from '../lib/agent-modes';
import { Field } from './Field';
import { Switch } from './Switch';
import { Button } from './ui/Button';

// One agent mode (plan 101 slice 06) — presenter for `<AgentModesPanel/>`. The
// switch, the source and revision that won, one line saying what the mode runs
// (so what switching it off holds), a warning when the server flags the winning
// row invalid, the tool checklist bounded by the mode's ceiling (floor tools
// checked and locked), the tools the server dropped, and this row's own refusal. Every act is a callback; the container writes.

export type AgentModeRowProps = {
  readonly view: AgentModeView;
  /** Prefix for this row's control ids — unique per panel. */
  readonly idPrefix: string;
  readonly busy: boolean;
  readonly error: string | null;
  /** Translated reset label, or null when this scope owns no row to reset. */
  readonly resetLabel: string | null;
  readonly onToggle: () => void;
  readonly onTool: (tool: string, on: boolean) => void;
  readonly onReset: () => void;
};

export function AgentModeRow(props: AgentModeRowProps): JSX.Element {
  const name = (): string => t(`agent_modes.mode.${props.view.key}`);
  const nameId = (): string => `${props.idPrefix}-name`;

  return (
    <li class="agent-mode" aria-labelledby={nameId()}>
      <div class="agent-mode__head">
        <span class="agent-mode__switch">
          <Switch
            checked={props.view.enabled}
            disabled={props.busy}
            aria-labelledby={nameId()}
            stateLabel={props.view.enabled ? t('agent_modes.on') : t('agent_modes.off')}
            // Controlled: the switch repaints from the re-read, never from the click,
            // so a refused write cannot leave it showing a state the server lacks.
            onActivate={() => props.onToggle()}
          />
          <span id={nameId()} class="agent-mode__name">
            {name()}
          </span>
        </span>
        <span class="agent-mode__meta">
          <span>{t(`agent_modes.source.${props.view.source}`)}</span>
          <span>{t('agent_modes.revision', { revision: props.view.revision })}</span>
        </span>
        <Show when={props.resetLabel}>
          {(label) => (
            <Button variant="ghost" size="sm" disabled={props.busy} onClick={() => props.onReset()}>
              {label()}
            </Button>
          )}
        </Show>
      </div>
      <p class="agent-mode__note">{t(`agent_modes.holds.${props.view.key}`)}</p>
      <Show when={props.view.invalid}>
        {(invalid) => (
          <p class="agent-mode__warn">{t('agent_modes.invalid', { reason: invalid().reason })}</p>
        )}
      </Show>

      <Show
        when={toolChoices(props.view).length > 0}
        fallback={<p class="agent-mode__note">{t('agent_modes.reviewer_note')}</p>}
      >
        <Field
          as="fieldset"
          id={`${props.idPrefix}-tools`}
          label={t('agent_modes.tools_legend', { mode: name() })}
        >
          {(a11y) => (
            <ul class="agent-mode__tools">
              <For each={toolChoices(props.view)}>
                {(choice) => (
                  <li>
                    <label class="agent-mode__tool">
                      <input
                        id={`${a11y.id}-${choice.tool}`}
                        type="checkbox"
                        checked={choice.checked}
                        disabled={props.busy || choice.floor}
                        onChange={(e) => {
                          const on = e.currentTarget.checked;
                          e.currentTarget.checked = choice.checked;
                          props.onTool(choice.tool, on);
                        }}
                      />
                      <span class="agent-mode__tool-name">{choice.tool}</span>
                      <Show when={choice.floor}>
                        <span class="agent-mode__floor">{t('agent_modes.floor')}</span>
                      </Show>
                    </label>
                  </li>
                )}
              </For>
            </ul>
          )}
        </Field>
      </Show>

      <Show when={props.view.dropped_tools.length > 0}>
        <p class="agent-mode__note">
          {t('agent_modes.dropped', { tools: props.view.dropped_tools.join(', ') })}
        </p>
      </Show>
      <Show when={props.error}>
        {(message) => (
          <p class="field__error" role="alert">
            {message()}
          </p>
        )}
      </Show>
    </li>
  );
}
